import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { PlinkoModel } from '../model/plinko/PlinkoModel';
import { DEFAULT_THEME, type Theme } from '../theme';
import { PlinkoBoard } from '../widgets/plinko/PlinkoBoard';
import { PlinkoHudComponent } from './components/PlinkoHudComponent';
import { DEFAULT_PLINKO_MULTIPLIERS } from './fixtures/plinkoFixtures';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 500;
const BET_PER_DROP = 10;
const ROWS = 8;

/**
 * Plinko demo — drop a ball through 8 rows of pegs into one of 9 slots.
 * Edge slots pay 10× bet; centre pays 0.4× bet. Binomial walk per drop
 * means the centre slot catches the ball most often.
 *
 * <p>The {@link PlinkoModel} owns the math + RNG; the {@link PlinkoBoard}
 * widget owns the visual + animation. The scene wires them together and
 * gates new drops while a ball is in flight.
 */
export class PlinkoDemoScene extends SceneBase {
  private board: PlinkoBoard | null = null;
  private hud: PlinkoHudComponent | null = null;
  private readonly random: Random;
  private readonly model: PlinkoModel;
  private modelSubscriptions: Disposable[] = [];
  private dropping = false;
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.model = new PlinkoModel(
      {
        startingBalance: profile?.balance ?? STARTING_BALANCE,
        bet: BET_PER_DROP,
        rows: ROWS,
        slotMultipliers: DEFAULT_PLINKO_MULTIPLIERS,
      },
      this.random,
    );
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected buildContent(): void {
    this.board = new PlinkoBoard({
      rows: ROWS,
      slotMultipliers: DEFAULT_PLINKO_MULTIPLIERS,
      pegSpacing: this.model.pegSpacingPx,
      accentColor: this.theme.palette.accent,
      ballColor: this.theme.palette.warm,
    });
    this.app.stage.addChild(this.board);

    this.hud = new PlinkoHudComponent(this.model, {
      onDrop: () => void this.handleDrop(),
    });
    this.app.stage.addChild(this.hud);

    this.modelSubscriptions.push(
      this.model.events.on('ball-dropped', (event) => {
        // Drive the visual descent now that the model has resolved the slot.
        void this.runBallAnimation(event.path, event.slot, event.multiplier, event.winnings);
      }),
    );
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      void this.handleDrop();
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    for (const sub of this.modelSubscriptions) sub.dispose();
    this.modelSubscriptions = [];
    this.model.dispose();
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    if (this.board) {
      this.board.destroy({ children: true });
      this.board = null;
    }
  }

  private layout(): void {
    if (!this.board || !this.hud) return;
    const { width, height } = this.app.renderer;
    const naturalWidth = this.board.totalWidth();
    const naturalHeight = this.board.totalHeight();
    const horizontalGutter = 24;
    const scale = Math.min(1, (width - horizontalGutter * 2) / naturalWidth);
    this.board.scale.set(scale);
    const scaledHeight = naturalHeight * scale;
    // Board origin is at the top-centre peg; place it so the whole field
    // is centred horizontally and vertically.
    this.board.x = Math.round(width / 2);
    this.board.y = Math.round((height - scaledHeight) / 2 - 40 + 16);

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: this.board.y,
      machineBottom: this.board.y + scaledHeight,
      spinButtonY: Math.round(this.board.y + scaledHeight + 50),
    };
    this.hud.layout(ctx);
  }

  private handleDrop(): void {
    if (this.dropping) return;
    if (!this.board) return;
    const result = this.model.drop();
    if (!result) return;
    // The 'ball-dropped' subscription kicks off the visual animation —
    // we just need to lock the button while that runs.
    this.dropping = true;
    this.hud?.setDropping(true);
  }

  private async runBallAnimation(
    path: readonly ('L' | 'R')[],
    slot: number,
    multiplier: number,
    winnings: number,
  ): Promise<void> {
    if (!this.board) return;
    await this.board.drop(path);
    this.dropping = false;
    this.hud?.setDropping(false);
    this.model.emitBallLanded(slot, multiplier, winnings);
    // Hide the ball after a brief beat so the player sees where it landed
    // before the next drop.
    setTimeout(() => this.board?.resetBall(), 600);
  }
}
